"use client";

import {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
  ReactNode,
} from "react";
import { useAuth } from "./AuthContext";

interface BrandKitData {
  companyDescription: string;
  company_service: string;
  websiteUrls: string[];
  socialUrls: {
    facebook: string;
    instagram: string;
    twitter: string;
    linkedin: string;
    youtube: string;
    tiktok: string;
  };
  uploadedFiles: {
    logos: File[];
    images: File[];
    videos: File[];
    documents: File[];
  };
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  uploadedFilesUpdated: any[];
}

interface BrandKitContextType {
  brandKitData: BrandKitData;
  setBrandKitData: (data: Partial<BrandKitData>) => void;
  resetBrandKitData: () => void;
  saveCompanyInfo: () => Promise<void>;
  saveImagesAndLogos: () => Promise<void>;
  saveDocuments: () => Promise<void>;
  deleteImage: (id: number) => Promise<void>;
  isLoading: boolean;
  fetchBrandKitData: () => Promise<void>;
  brandKitStatus: string;
  brandKitDescriptionFilled: boolean;
}

const BrandKitContext = createContext<BrandKitContextType | undefined>(
  undefined
);

const initialBrandKitData: BrandKitData = {
  companyDescription: "",
  company_service: "",
  websiteUrls: [],
  socialUrls: {
    facebook: "",
    instagram: "",
    twitter: "",
    linkedin: "",
    youtube: "",
    tiktok: "",
  },
  uploadedFiles: {
    logos: [],
    images: [],
    videos: [],
    documents: [],
  },
  uploadedFilesUpdated: [],
};

export const BrandKitProvider = ({ children }: { children: ReactNode }) => {
  const { user } = useAuth();
  const [brandKitData, setBrandKitDataState] =
    useState<BrandKitData>(initialBrandKitData);
  const [isLoading, setIsLoading] = useState(false);
  const [brandKitStatus, setBrandKitStatus] = useState<string>("");
  const [brandKitDescriptionFilled, setBrandKitDescriptionFilled] =
    useState(false);

  const fetchImages = useCallback(async () => {
    if (!user?.token) {
      return null;
    }

    try {
      const response = await fetch(
        "https://connect-api.winlocal.com/api/image-analysis/my-images",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          // No images exist yet
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();

      // Transform the response to match the expected structure
      if (result.success && result.data && result.data.data) {
        const images = result.data.data.map(
          (image: {
            id: number;
            original_name: string;
            file_size: number;
            file_url?: string;
            thumbnail_url?: string;
            labels?: Array<{ name: string; confidence: number }>;
          }) => {
            // Determine asset type based on labels (if it has "Logo" label, it's a logo)
            const isLogo = image.labels?.some(
              (label) => label.name.toLowerCase() === "logo"
            );

            return {
              id: image.id,
              asset_type: isLogo
                ? "logos_and_brand_marks"
                : "images_and_graphics",
              original_name: image.original_name,
              file_size: image.file_size,
              file_url: image.file_url,
              thumbnail_url: image.thumbnail_url,
            };
          }
        );

        return images;
      }

      return null;
    } catch (error) {
      console.error("Error fetching images:", error);
      return null;
    }
  }, [user?.token]);

  const fetchDocuments = useCallback(async () => {
    if (!user?.token) {
      return null;
    }

    try {
      const response = await fetch(
        "https://connect-api.winlocal.com/api/brand-assets/documents",
        {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        if (response.status === 404) {
          return null;
        }
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      const result = await response.json();

      if (result.success && result.data?.files) {
        return result.data.files.map(
          (file: {
            original_name: string;
            file_size: number;
            file_url?: string;
          }) => ({
            asset_type: "documents_and_guidelines",
            original_name: file.original_name,
            file_size: file.file_size,
            file_url: file.file_url,
          })
        );
      }

      return null;
    } catch (error) {
      console.error("Error fetching documents:", error);
      return null;
    }
  }, [user?.token]);

  // Helper function to update status based on current data
  const updateBrandKitStatus = useCallback((data: BrandKitData) => {
    const hasCompanyDescription =
      (data.companyDescription || "").trim().length > 0;
    const hasWebsiteUrls = (data.websiteUrls || []).length > 0;
    const hasSocialUrls = Object.values(data.socialUrls || {}).some(
      (url) => url && url.trim().length > 0
    );
    const hasAssets = (data.uploadedFilesUpdated || []).length > 0;

    if (hasCompanyDescription || hasWebsiteUrls || hasSocialUrls || hasAssets) {
      setBrandKitStatus("filled");
    } else {
      setBrandKitStatus("empty");
    }
  }, []);

  const fetchBrandKitData = useCallback(async () => {
    if (!user?.token) {
      return;
    }

    setIsLoading(true);
    try {
      // Fetch brand knowledge, images, and documents in parallel
      const [knowledgeResponse, imagesData, documentsData] = await Promise.all([
        fetch("https://connect-api.winlocal.com/api/brand-knowledge", {
          method: "GET",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }),
        fetchImages(),
        fetchDocuments(),
      ]);

      if (!knowledgeResponse.ok) {
        if (knowledgeResponse.status === 404) {
          // No brand kit data exists yet, keep initial values
          setBrandKitStatus("empty");
          return;
        }
        const errorData = await knowledgeResponse.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${knowledgeResponse.status}`
        );
      }

      const result = await knowledgeResponse.json();

      if (result.success && result.data) {
        const apiData = result.data;

        // Parse URLs from the urls array
        const urls = apiData.urls || [];

        // Extract website URLs (type === "website")
        const websiteUrls = urls
          .filter(
            (urlItem: { type: string; url: string }) =>
              urlItem.type === "website"
          )
          .map((urlItem: { url: string }) => urlItem.url)
          .filter((url: string) => url && url.trim());

        // Extract social media URLs by type
        const getSocialUrl = (type: string) => {
          const urlItem = urls.find(
            (item: { type: string; url: string }) => item.type === type
          );
          return urlItem?.url || "";
        };

        const uploadedFiles = {
          logos: [],
          images: [],
          videos: [],
          documents: [],
        };

        const companyDescription = apiData.company_description || "";
        const company_service = apiData.company_services || "";
        const socialUrls = {
          facebook: getSocialUrl("facebook"),
          instagram: getSocialUrl("instagram"),
          twitter: getSocialUrl("twitter"),
          linkedin: getSocialUrl("linkedin"),
          youtube: getSocialUrl("youtube"),
          tiktok: getSocialUrl("tiktok"),
        };

        // Combine images from new endpoint with documents from documents endpoint
        const images = imagesData || [];
        const documents = documentsData || [];
        const uploadedFilesUpdated = [...images, ...documents];

        if (companyDescription.trim().length > 0) {
          setBrandKitDescriptionFilled(true);
        } else {
          setBrandKitDescriptionFilled(false);
        }

        setBrandKitDataState({
          companyDescription: companyDescription,
          company_service: company_service,
          websiteUrls: websiteUrls,
          socialUrls: socialUrls,
          uploadedFiles: uploadedFiles,
          uploadedFilesUpdated: uploadedFilesUpdated,
        });

        // Brand kit is considered "filled" if it has any meaningful data
        // Use the updateBrandKitStatus helper to ensure consistency
        updateBrandKitStatus({
          companyDescription: companyDescription,
          company_service: company_service,
          websiteUrls: websiteUrls,
          socialUrls: socialUrls,
          uploadedFiles: uploadedFiles,
          uploadedFilesUpdated: uploadedFilesUpdated,
        });
      }
    } catch (error) {
      console.error("Error fetching brand kit data:", error);
      // Keep initial values on error
    } finally {
      setIsLoading(false);
    }
  }, [user?.token, fetchImages, fetchDocuments, updateBrandKitStatus]);

  const deleteImage = useCallback(
    async (id: number) => {
      if (!user?.token) {
        throw new Error("No authentication token found");
      }

      const response = await fetch(
        `https://connect-api.winlocal.com/api/image-analysis/my-images/${id}`,
        {
          method: "DELETE",
          headers: {
            Authorization: `Bearer ${user.token}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message || `HTTP error! status: ${response.status}`
        );
      }

      await fetchBrandKitData();
    },
    [user?.token, fetchBrandKitData]
  );

  // Fetch brand kit data when component mounts or user changes
  useEffect(() => {
    if (user?.token) {
      fetchBrandKitData();
    }
  }, [user?.token, fetchBrandKitData]);

  const setBrandKitData = (data: Partial<BrandKitData>) => {
    setBrandKitDataState((prev) => ({
      ...prev,
      ...data,
    }));
  };

  const resetBrandKitData = () => {
    setBrandKitDataState(initialBrandKitData);
  };

  const saveCompanyInfo = async () => {
    if (!user?.token) {
      throw new Error("No authentication token found");
    }

    const requestBody = {
      company_description: brandKitData.companyDescription,
      company_services: brandKitData.company_service,
      website_url: brandKitData.websiteUrls.filter((url) => url && url.trim()),
      facebook_url: brandKitData.socialUrls.facebook,
      instagram_url: brandKitData.socialUrls.instagram,
      twitter_url: brandKitData.socialUrls.twitter,
      linkedin_url: brandKitData.socialUrls.linkedin,
      youtube_url: brandKitData.socialUrls.youtube,
      tiktok_url: brandKitData.socialUrls.tiktok,
    };

    const response = await fetch(
      "https://connect-api.winlocal.com/api/brand-knowledge",
      {
        method: "PUT",
        headers: {
          Authorization: `Bearer ${user.token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(requestBody),
      }
    );

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({}));
      throw new Error(
        errorData.message || `HTTP error! status: ${response.status}`
      );
    }

    const result = await response.json();

    // Update status immediately after saving
    updateBrandKitStatus(brandKitData);

    return result;
  };

  const saveImagesAndLogos = async () => {
    if (!user?.token) {
      throw new Error("No authentication token found");
    }

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
    const MIN_FILES = 1;
    const MAX_FILES = 10;

    const filesToUpload = [
      ...brandKitData.uploadedFiles.images,
      ...brandKitData.uploadedFiles.logos,
    ];

    if (filesToUpload.length === 0) {
      throw new Error("No image or logo files to upload");
    }

    if (filesToUpload.length < MIN_FILES || filesToUpload.length > MAX_FILES) {
      throw new Error(
        `Images & Logos must have between ${MIN_FILES} and ${MAX_FILES} files`
      );
    }

    for (const file of filesToUpload) {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File ${file.name} exceeds maximum size of 50MB`);
      }
    }

    // The new endpoint expects a single file field named "image".
    // Upload files one by one to match the API contract.
    for (const file of filesToUpload) {
      const formData = new FormData();
      formData.append("image", file);

      const response = await fetch(
        "https://connect-api.winlocal.com/api/image-analysis/store",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user.token}`,
            // Content-Type not set to allow FormData boundary
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `HTTP error! status: ${response.status} for images`
        );
      }

      await response.json();
    }

    // Status will be updated after refetch in the component that calls this
    // Assets are now in the API, so refetch will update uploadedFilesUpdated
  };

  const saveDocuments = async () => {
    if (!user?.token) {
      throw new Error("No authentication token found");
    }

    const MAX_FILE_SIZE = 50 * 1024 * 1024; // 50MB in bytes
    const MIN_FILES = 1;
    const MAX_FILES = 10;

    const files = brandKitData.uploadedFiles.documents;

    if (files.length === 0) {
      throw new Error("No document files to upload");
    }

    if (files.length < MIN_FILES || files.length > MAX_FILES) {
      throw new Error(
        `Documents must have between ${MIN_FILES} and ${MAX_FILES} files`
      );
    }

    // Validate file sizes
    for (const file of files) {
      if (file.size > MAX_FILE_SIZE) {
        throw new Error(`File ${file.name} exceeds maximum size of 50MB`);
      }
    }

    // Upload each file individually
    const uploadPromises = files.map(async (file) => {
      const formData = new FormData();
      formData.append("file", file);
      formData.append("file_type", "file");

      const response = await fetch(
        "https://connect-api.winlocal.com/api/brand-assets/documents",
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${user.token}`,
            // Don't set Content-Type header - browser will set it with boundary for FormData
          },
          body: formData,
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.message ||
            `HTTP error! status: ${response.status} for ${file.name}`
        );
      }

      await response.json();
    });

    await Promise.all(uploadPromises);

    // Status will be updated after refetch in the component that calls this
    // Assets are now in the API, so refetch will update uploadedFilesUpdated
  };

  return (
    <BrandKitContext.Provider
      value={{
        brandKitData,
        setBrandKitData,
        resetBrandKitData,
        saveCompanyInfo,
        saveImagesAndLogos,
        saveDocuments,
        deleteImage,
        isLoading,
        fetchBrandKitData,
        brandKitStatus,
        brandKitDescriptionFilled,
      }}
    >
      {children}
    </BrandKitContext.Provider>
  );
};

export const useBrandKit = () => {
  const context = useContext(BrandKitContext);
  if (context === undefined) {
    throw new Error("useBrandKit must be used within a BrandKitProvider");
  }
  return context;
};
